import { 
  Component,
  OnInit,
  ContentChildren,
  QueryList,
  Input,
  AfterContentInit,
  ChangeDetectionStrategy,
  OnDestroy,
  ChangeDetectorRef,
  Output,
  EventEmitter
} from '@angular/core';
import { TabComponent } from '../tab/tab.component';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil, tap, pairwise, filter } from 'rxjs/operators';

class TabItem {
  index: number;
  label: string;
  tab: TabComponent;
}

@Component({
  selector: 'app-tabs',
  templateUrl: './tabs.component.html',
  styleUrls: ['./tabs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TabsComponent implements OnInit, AfterContentInit, OnDestroy {
  @ContentChildren(TabComponent)
  _tabs: QueryList<TabComponent>;
  
  @Input()
  public display: 'normal' | 'inline' = 'normal';

  @Input()
  public get activeTab(): string | number {
    return this._activeTab.value;
  }
  public set activeTab(value: string | number) {
    if (value != null) {
      if (typeof value === 'string') {
        value = value.toUpperCase();
      }
      this._activeTab.next(value);
    }
  }

  @Output()
  activeTabChange = new EventEmitter<string | number>();

  _activeTab: BehaviorSubject<string | number> = new BehaviorSubject<string | number>(0);

  @Input()
  public tabSize: 'small' | 'medium' = 'small';

  public get tabItems(): TabItem[] {
    return this._tabItems;
  }
  public set tabItems(value: TabItem[]) {
    this._tabItems = value;
  }
  private _tabItems: TabItem[];

  private ngUnsubscribe = new Subject();

  constructor(
    private changeDetector: ChangeDetectorRef
  ) { }


  ngOnInit() {
  }


  ngAfterContentInit() {
    this._tabs.changes
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((tabs: QueryList<TabComponent>) => {
        this.mapTabs();
        this.changeDetector.markForCheck();
      });

    this.mapTabs();

    this._activeTab
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((tab) => {
        this.showTab(tab);
      });

    // notify selected tab
    this._activeTab
      .pipe(
        pairwise(),
        filter(([previousTab, currentTab]) => previousTab !== currentTab),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(([previousTab, currentTab]) => {
        this.activeTabChange.next(currentTab);
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }


  handleTabSelect(tabItem: TabItem) {
    this._activeTab.next(tabItem.index);
  }

  mapTabs() {
    this.tabItems = this._tabs.map((tab, index) => {
      return {
        index: index,
        label: tab.label || `Item ${index}`,
        tab: tab
      };
    });
  }

  
  showTab(tabSelector: string | number) {
    this._tabs.forEach((_tab, _index) => {
      switch (typeof tabSelector) {
        case 'number':
          _tab.isActive = (tabSelector === _index);
          break;
        case 'string':
          _tab.isActive = (tabSelector.toString().toUpperCase() === _tab.label.toUpperCase());
          break;
      }
    });

    this.changeDetector.markForCheck();
  }

  isTabActive(tabItem: TabItem) {
    const tabValue = (typeof this._activeTab.value === 'string') ? tabItem.label.toUpperCase() : tabItem.index;
    return tabValue === this._activeTab.value;
  }
}
